import { useCallback, useState } from 'react';
import { FunnelIcon } from '@phosphor-icons/react';
import Button from 'src/components/ui/Button';
import Dropdown from 'src/components/ui/Dropdown';
import Popover from 'src/components/ui/Popover';
import { usePostHog } from 'src/lib/posthog';
import { useFilterOptions, FilterOption } from 'src/shared/filters/databaseFilterOptions';
import { FilterAggregations } from 'src/features/chat/types';

interface WikiFilterProps {
  filters: Record<string, string[]>;
  onFilterChange: (key: string, value: string[]) => void;
  aggregations?: FilterAggregations;
  className?: string;
  originEnabled?: boolean;
  showScrapedOption?: boolean;
  hideCategoryWhenSingle?: boolean;
  mode?: 'browse' | 'manage';
}

export default function WikiFilter({
  filters,
  onFilterChange,
  aggregations,
  className = '',
  originEnabled = false,
  showScrapedOption = false,
  hideCategoryWhenSingle = false,
  mode = 'browse'
}: WikiFilterProps) {
  const posthog = usePostHog();
  const filterOptions = useFilterOptions(aggregations);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLElement | null>(null);

  const toOptions = useCallback(
    (source: FilterOption[]) =>
      source.map((opt) => ({
        label: opt.label,
        value: opt.value,
        disabled: opt.disabled,
        count: opt.count
      })),
    []
  );

  const handleChange = (key: string) => (value: string | string[]) => {
    const next = Array.isArray(value) ? value : value ? [value] : [];
    onFilterChange(key, next);
    const eventName = mode === 'manage' ? 'data_library_filter_changed' : 'wiki_filter_changed';
    posthog?.capture(eventName, { filter: key, value: next });
  };

  const sourceCounts = aggregations?.source_counts || {};
  const sourceOptions = [
    Number(sourceCounts.sharepoint) > 0 && {
      label: 'SharePoint',
      value: 'sharepoint',
      count: sourceCounts.sharepoint,
      disabled: false
    },
    showScrapedOption &&
      Number(sourceCounts.scraped) > 0 && {
        label: 'Scraped',
        value: 'scraped',
        count: sourceCounts.scraped,
        disabled: false
      },
    Number(sourceCounts.manual) > 0 && {
      label: 'Manual Upload',
      value: 'manual',
      count: sourceCounts.manual,
      disabled: false
    }
  ].filter(Boolean) as { label: string; value: string; count: number; disabled: boolean }[];

  const categoryOptions = toOptions(filterOptions.categories);
  const filtersToShow = [
    originEnabled && sourceOptions.length > 0 && { key: 'origin', placeholder: 'Source', options: sourceOptions },
    (!hideCategoryWhenSingle || categoryOptions.length > 1) && {
      key: 'category',
      placeholder: 'Categories',
      options: categoryOptions
    },
    { key: 'domain', placeholder: 'Domains', options: toOptions(filterOptions.domains) },
    { key: 'authority', placeholder: 'Authority', options: toOptions(filterOptions.authorities) },
    { key: 'subDomain', placeholder: 'Sub domains', options: toOptions(filterOptions.subDomains) }
  ].filter((item): item is { key: string; placeholder: string; options: ReturnType<typeof toOptions> } =>
    Boolean(item && item.options.length > 0)
  );

  const hasActive = filtersToShow.some(({ key }) => (filters[key] || []).length > 0);

  if (filtersToShow.length === 0) return null;

  const dropdowns = (width: string) =>
    filtersToShow.map(({ key, placeholder, options }) => (
      <Dropdown
        key={key}
        multiple
        options={options}
        value={filters[key] || []}
        onChange={handleChange(key)}
        placeholder={placeholder}
        ariaLabel={placeholder}
        width={width}
      />
    ));

  return (
    <div className={`flex min-w-0 shrink-0 flex-wrap items-center gap-2 ${className}`}>
      <div className="hidden min-w-0 items-center gap-2 @[1100px]:flex">{dropdowns('w-40')}</div>
      <span ref={setFilterButtonEl} className="shrink-0 @[1100px]:hidden">
        <Button
          type="button"
          variant={hasActive ? 'primary' : 'outline'}
          size="md"
          icon={FunnelIcon}
          aria-label="Filters"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
        />
      </span>
      <Popover
        open={filtersOpen}
        anchorEl={filterButtonEl}
        onClose={() => setFiltersOpen(false)}
        placement="bottom-end"
        className="w-[220px] p-3"
      >
        <div className="flex flex-col gap-2">{dropdowns('w-full')}</div>
      </Popover>
    </div>
  );
}
